/**
 * Promotions — design/PROTOTYPE-SPEC.md §12. Pro and up.
 *
 * ## Suggestions come first, deliberately
 *
 * The prototype puts suggested promotions above the list, and that ordering is
 * the feature. A list of promotions is a form someone has to have an idea
 * before using; a list of *suggested* promotions is the app noticing that six
 * bottles of serum expire in a month and saying so. A clinic that opens this
 * page with nothing running should still leave with something worth doing.
 *
 * Each suggestion prefills the form rather than creating anything. The discount
 * is the clinic's decision — this is money — so the app proposes and a person
 * accepts.
 *
 * The rules behind the suggestions live in `packages/core` and are tested
 * there; the API only assembles the inputs.
 */
import { can, type Role as CoreRole } from '@inventory-tracker/core';
import { useCallback, useEffect, useState } from 'react';
import {
  createPromotion,
  deletePromotion,
  endPromotion,
  listPromotionSuggestions,
  listPromotions,
  listProducts,
  type CreatePromotionInput,
  type Promotion,
  type PromotionSuggestion,
  type ProductRow,
} from '../lib/api.js';
import { PlanRequiredError, type Me } from '../lib/api.js';
import { MarketingAssistant } from './MarketingAssistant.js';
import { PlanLocked } from './PlanLocked.js';
import type { Plan } from '@inventory-tracker/core';
import { c, field, mono, primaryButton, sans } from '../theme.js';
import {
  cardStyle,
  formatDate,
  money,
  penceToPounds,
  poundsToPence,
  smallButton,
} from './format.js';

interface Props {
  me: Me;
  role: CoreRole;
  onChanged: () => void;
  refreshToken: number;
}

const STATUS_TONE: Record<Promotion['status'], { bg: string; fg: string }> = {
  active: { bg: c.okBg, fg: c.okText },
  scheduled: { bg: c.amberBg, fg: c.amberText },
  ended: { bg: c.well, fg: c.muted2 },
};

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '9px 12px',
  fontSize: 12.5,
  textTransform: 'lowercase',
  color: c.muted2,
  fontWeight: 400,
};
const td: React.CSSProperties = { padding: '11px 12px', fontSize: 13.5 };

/** Today in the browser's timezone, as an ISO date, for form defaults only. */
function todayISO(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());
}

export function PromotionsPage({ me, role, onChanged, refreshToken }: Props) {
  const mayEdit = can(role, 'manageProducts');
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [suggestions, setSuggestions] = useState<PromotionSuggestion[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** Set when the API answered 402 — the plan cannot reach this feature. */
  const [locked, setLocked] = useState<Plan | null>(null);
  const [form, setForm] = useState<Partial<CreatePromotionInput> | null>(null);

  const reload = useCallback(async () => {
    const [p, s, prod] = await Promise.all([
      listPromotions(),
      listPromotionSuggestions(),
      listProducts(),
    ]);
    setPromotions(p.promotions);
    setSuggestions(s.suggestions);
    setProducts(prod.products);
  }, []);

  useEffect(() => {
    void reload().catch((e: unknown) => {
      // 402 is not a failure, it is an answer: this plan does not include the
      // feature. Rendering it as an error would tell a Starter clinic something
      // broke, when in fact there is a thing to buy.
      if (e instanceof PlanRequiredError) {
        setLocked(e.requiredPlan);
        return;
      }
      setError(e instanceof Error ? e.message : 'Could not load your promotions');
    });
  }, [reload, refreshToken]);

  async function mutate(work: () => Promise<unknown>): Promise<void> {
    setError(null);
    try {
      await work();
      await reload();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work');
    }
  }

  const live = promotions.filter((p) => p.status !== 'ended');
  const past = promotions.filter((p) => p.status === 'ended');

  /**
   * The locked state. Deliberately describes the feature rather than just
   * refusing: someone who navigated here is interested, and "Promotions needs
   * Pro" alone tells them nothing about whether it is worth having.
   *
   * The spec wants an inline upgrade checkout here. That needs Stripe, which is
   * Phase 3 — so this stops at explaining, rather than showing a button that
   * cannot take a payment.
   */
  if (locked) {
    return (
      <PlanLocked
        requiredPlan={locked}
        cycle={me.subscription.cycle}
        role={role}
        title="Shift the stock that is not moving"
        blurb="Run time-boxed offers on your retail lines and see what each one was worth. The app watches your shelves and suggests them: stock coming up to expiry, lines that have not sold in six weeks, and anything you are holding far more of than you are selling."
      />
    );
  }

  return (
    <div data-tour="promos">
      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13.5,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}

      {/* Suggestions first — see the header note. */}
      {suggestions.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 6px' }}>
            Worth promoting
          </h2>
          <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 16px', lineHeight: 1.7 }}>
            Built from what is actually on your shelves. Nothing happens until you set one up.
          </p>
          {suggestions.map((s) => (
            <div
              key={s.productId}
              style={{
                borderTop: `1px solid ${c.border}`,
                padding: '13px 0',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 3 }}>
                  <strong style={{ fontSize: 14 }}>{s.productName}</strong>
                  <span
                    style={{
                      padding: '3px 9px',
                      borderRadius: 980,
                      fontSize: 10,
                      fontWeight: 400,
                      textTransform: 'lowercase', fontFamily: mono,
                      background: c.dangerBg,
                      border: `1px solid ${c.dangerLine}`,
                      color: c.danger,
                    }}
                  >
                    {s.flag}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: c.muted, lineHeight: 1.6 }}>{s.why}</div>
                <div style={{ fontSize: 13, color: c.muted2, marginTop: 3 }}>
                  Suggested: {s.offer}
                </div>
              </div>
              {mayEdit && (
                <button
                  type="button"
                  onClick={() =>
                    setForm({
                      productId: s.productId,
                      kind: s.prefill.kind,
                      value: s.prefill.value,
                      startsOn: todayISO(),
                      endsOn: s.prefill.endsOn,
                      note: s.prefill.note,
                    })
                  }
                  style={{ ...smallButton, whiteSpace: 'nowrap' }}
                >
                  Set this up
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ ...cardStyle, marginBottom: 16 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 6,
          }}
        >
          <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: 0 }}>
            Promotions
          </h2>
          {mayEdit && (
            <button
              type="button"
              onClick={() => setForm({ kind: 'percent', startsOn: todayISO() })}
              style={{ ...primaryButton, width: 'auto', height: 38, padding: '0 16px' }}
            >
              New promotion
            </button>
          )}
        </div>

        {promotions.length === 0 ? (
          <p style={{ fontSize: 13.5, color: c.muted2, margin: '10px 0 0' }}>
            Nothing running yet.{' '}
            {suggestions.length > 0
              ? 'The suggestions above are a good place to start.'
              : 'Set one up when you want to shift a product.'}
          </p>
        ) : (
          <PromotionTable
            rows={live}
            emptyNote="Nothing running or scheduled right now."
            mayEdit={mayEdit}
            onEnd={(id) => mutate(() => endPromotion(id))}
            onDelete={(id) => mutate(() => deletePromotion(id))}
          />
        )}
      </div>

      {past.length > 0 && (
        <div style={cardStyle}>
          <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 21, margin: '0 0 6px' }}>
            Finished
          </h2>
          <p style={{ fontSize: 13.5, color: c.muted, margin: '0 0 10px', lineHeight: 1.7 }}>
            Kept so you can see what you ran and what it was worth.
          </p>
          <PromotionTable
            rows={past}
            emptyNote=""
            mayEdit={false}
            onEnd={() => {}}
            onDelete={() => {}}
          />
        </div>
      )}

      {form && (
        <PromotionForm
          draft={form}
          products={products}
          onCancel={() => setForm(null)}
          onSave={async (input) => {
            await mutate(() => createPromotion(input));
            setForm(null);
          }}
        />
      )}

      {/* Below the promotions it writes about. It reads the live ones, so a
          promotion created above is in the very next generation. */}
      <MarketingAssistant clinicName={me.account.clinicName} />
    </div>
  );
}

function PromotionTable({
  rows,
  emptyNote,
  mayEdit,
  onEnd,
  onDelete,
}: {
  rows: Promotion[];
  emptyNote: string;
  mayEdit: boolean;
  onEnd: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  if (rows.length === 0) {
    return emptyNote ? (
      <p style={{ fontSize: 13.5, color: c.muted2, margin: '10px 0 0' }}>{emptyNote}</p>
    ) : null;
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={th}>Product</th>
          <th style={th}>Offer</th>
          <th style={th}>Price</th>
          <th style={th}>Dates</th>
          <th style={th}>Status</th>
          <th style={{ ...th, textAlign: 'right' }} />
        </tr>
      </thead>
      <tbody>
        {rows.map((p) => {
          const tone = STATUS_TONE[p.status];
          return (
            <tr key={p.id} style={{ borderTop: `1px solid ${c.border}` }}>
              <td style={td}>
                <strong>{p.productName}</strong>
                {p.note && (
                  <div style={{ color: c.muted2, fontSize: 12, marginTop: 2 }}>{p.note}</div>
                )}
              </td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                {p.kind === 'percent' ? `${p.value}% off` : `${money(p.value)} off`}
              </td>
              <td style={{ ...td, whiteSpace: 'nowrap' }}>
                {p.promoPricePence === null ? (
                  <span style={{ color: c.muted2 }}>—</span>
                ) : (
                  <>
                    <strong>{money(p.promoPricePence)}</strong>
                    {p.retailPricePence !== null && (
                      <span
                        style={{ color: c.muted2, textDecoration: 'line-through', marginLeft: 6 }}
                      >
                        {money(p.retailPricePence)}
                      </span>
                    )}
                  </>
                )}
              </td>
              <td style={{ ...td, whiteSpace: 'nowrap', color: c.muted }}>
                {formatDate(p.startsOn)}
                {p.endsOn ? ` – ${formatDate(p.endsOn)}` : ' – ongoing'}
              </td>
              <td style={td}>
                <span
                  style={{
                    padding: '3px 10px',
                    borderRadius: 980,
                    fontSize: 11,
                    fontWeight: 500,
                    background: tone.bg,
                    color: tone.fg,
                  }}
                >
                  {p.statusLabel}
                </span>
              </td>
              <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                {mayEdit && p.status === 'active' && (
                  <button type="button" onClick={() => onEnd(p.id)} style={smallButton}>
                    End now
                  </button>
                )}
                {mayEdit && p.status === 'scheduled' && (
                  <button
                    type="button"
                    onClick={() => onDelete(p.id)}
                    style={{ ...smallButton, color: c.danger }}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * The new-promotion form.
 *
 * Only retail products are offered. A promotion needs a retail price to
 * discount, and the API refuses one without it — so the choice is narrowed here
 * rather than letting someone pick a product and then be told no.
 */
function PromotionForm({
  draft,
  products,
  onCancel,
  onSave,
}: {
  draft: Partial<CreatePromotionInput>;
  products: ProductRow[];
  onCancel: () => void;
  onSave: (input: CreatePromotionInput) => Promise<void>;
}) {
  const sellable = products.filter((p) => p.retailPricePence !== null);
  const [productId, setProductId] = useState(draft.productId ?? sellable[0]?.id ?? '');
  const [kind, setKind] = useState<'percent' | 'amount'>(draft.kind ?? 'percent');
  const [value, setValue] = useState(
    draft.value === undefined
      ? '15'
      : draft.kind === 'amount'
        ? penceToPounds(draft.value)
        : String(draft.value),
  );
  const [startsOn, setStartsOn] = useState(draft.startsOn ?? todayISO());
  const [endsOn, setEndsOn] = useState(draft.endsOn ?? '');
  const [note, setNote] = useState(draft.note ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const product = sellable.find((p) => p.id === productId);

  /** Live preview, so the discount is a price before it is a promise. */
  const preview = (() => {
    if (!product || product.retailPricePence === null) return null;
    const off = kind === 'percent' ? Number(value) : poundsToPence(value);
    if (off === null || !Number.isFinite(off) || off <= 0) return null;
    const raw =
      kind === 'percent'
        ? (product.retailPricePence * (100 - off)) / 100
        : product.retailPricePence - off;
    return Math.max(0, Math.round(raw));
  })();

  async function submit() {
    if (!productId) {
      setError('Pick a product');
      return;
    }
    const numeric = kind === 'percent' ? Number(value) : poundsToPence(value);
    if (numeric === null || !Number.isFinite(numeric) || numeric < 1) {
      setError(kind === 'percent' ? 'Enter a percentage, like 15' : 'Enter an amount, like 5.00');
      return;
    }
    if (kind === 'percent' && numeric > 100) {
      setError('A percentage must be 1–100');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onSave({
        productId,
        kind,
        value: numeric,
        startsOn,
        endsOn: endsOn === '' ? null : endsOn,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-label="New promotion"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(20,26,22,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        zIndex: 50,
      }}
    >
      <div
        style={{ ...cardStyle, width: 'min(520px, 100%)', maxHeight: '90vh', overflowY: 'auto' }}
      >
        <h2 style={{ fontFamily: sans, letterSpacing: '-0.03em', fontWeight: 300, fontSize: 22, margin: '0 0 16px' }}>
          New promotion
        </h2>

        {error && (
          <div
            role="alert"
            style={{
              background: c.dangerBg,
              border: `1px solid ${c.dangerBorder}`,
              borderRadius: 12,
              padding: '10px 14px',
              marginBottom: 14,
              fontSize: 13,
              color: c.danger,
            }}
          >
            {error}
          </div>
        )}

        {sellable.length === 0 ? (
          <p style={{ fontSize: 13.5, color: c.muted, lineHeight: 1.7 }}>
            None of your products has a retail price yet, so there is nothing to discount. Set one
            on Manage stock first.
          </p>
        ) : (
          <>
            <label style={{ display: 'block', marginBottom: 12 }}>
              <span style={{ fontSize: 12, color: c.muted2, display: 'block', marginBottom: 5 }}>
                Product
              </span>
              <select
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                style={{ ...field, fontFamily: sans, width: '100%' }}
              >
                {sellable.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}, {money(p.retailPricePence!)}
                  </option>
                ))}
              </select>
            </label>

            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: 12, color: c.muted2, display: 'block', marginBottom: 5 }}>
                  Discount
                </span>
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as 'percent' | 'amount')}
                  style={{ ...field, fontFamily: sans, width: '100%' }}
                >
                  <option value="percent">Percent off</option>
                  <option value="amount">Amount off</option>
                </select>
              </label>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: 12, color: c.muted2, display: 'block', marginBottom: 5 }}>
                  {kind === 'percent' ? '% off' : '£ off'}
                </span>
                <input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  inputMode="decimal"
                  style={{ ...field, width: '100%' }}
                />
              </label>
            </div>

            {preview !== null && product && (
              <div
                style={{
                  background: c.well,
                  borderRadius: 12,
                  padding: '10px 14px',
                  marginBottom: 12,
                  fontSize: 13.5,
                }}
              >
                Customers pay <strong>{money(preview)}</strong>{' '}
                <span style={{ color: c.muted2, textDecoration: 'line-through' }}>
                  {money(product.retailPricePence!)}
                </span>
                <span style={{ color: c.muted, marginLeft: 8 }}>
                  saving {money(product.retailPricePence! - preview)}
                </span>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: 12, color: c.muted2, display: 'block', marginBottom: 5 }}>
                  Starts
                </span>
                <input
                  type="date"
                  value={startsOn}
                  onChange={(e) => setStartsOn(e.target.value)}
                  style={{ ...field, width: '100%' }}
                />
              </label>
              <label style={{ flex: 1 }}>
                <span style={{ fontSize: 12, color: c.muted2, display: 'block', marginBottom: 5 }}>
                  Ends (optional)
                </span>
                <input
                  type="date"
                  value={endsOn}
                  onChange={(e) => setEndsOn(e.target.value)}
                  style={{ ...field, width: '100%' }}
                />
              </label>
            </div>

            <label style={{ display: 'block', marginBottom: 18 }}>
              <span style={{ fontSize: 12, color: c.muted2, display: 'block', marginBottom: 5 }}>
                Note (optional)
              </span>
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Summer skin event"
                style={{ ...field, width: '100%' }}
              />
            </label>
          </>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ ...smallButton, height: 42, padding: '0 18px' }}
          >
            Cancel
          </button>
          {sellable.length > 0 && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void submit()}
              style={{
                ...primaryButton,
                width: 'auto',
                height: 42,
                padding: '0 20px',
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? 'Saving…' : 'Start promotion'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
